import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { api, type ApiError } from '../lib/api';
import type { SuperAdminUser } from '../types';

/**
 * Sessão do painel super-admin (F3-05 T08). Login por cookie Sanctum:
 * POST /auth/login (sessão) → GET /super-admin/me valida o Gate. Quem loga mas
 * não é super-admin recebe 403 no `me` e é rejeitado.
 */
export const useSuperAdminStore = defineStore('super-admin', () => {
    const user = ref<SuperAdminUser | null>(null);
    const booted = ref(false);
    const loading = ref(false);
    const error = ref<string | null>(null);

    const isAuthenticated = computed(() => user.value !== null);

    /** Tenta restaurar a sessão (cookie) ao montar a SPA. */
    async function bootstrap(): Promise<void> {
        try {
            const res = await api.get<{ user: SuperAdminUser }>('/super-admin/me');
            user.value = res.user;
        } catch {
            user.value = null;
        } finally {
            booted.value = true;
        }
    }

    async function login(email: string, password: string): Promise<boolean> {
        loading.value = true;
        error.value = null;
        try {
            await api.post('/auth/login', { email, password });
            // Sessão criada — confirma que é super-admin pelo Gate.
            const res = await api.get<{ user: SuperAdminUser }>('/super-admin/me');
            user.value = res.user;
            return true;
        } catch (e) {
            const err = e as ApiError;
            user.value = null;
            error.value = err.status === 403
                ? 'Esta conta não tem acesso ao painel super-admin.'
                : err.status === 422 || err.status === 429
                    ? (err.message || 'E-mail ou senha incorretos.')
                    : 'Não foi possível entrar. Tente novamente.';
            return false;
        } finally {
            loading.value = false;
        }
    }

    async function logout(): Promise<void> {
        try {
            await api.post('/auth/logout');
        } catch {
            // ignora — limpamos o estado local de qualquer forma
        }
        user.value = null;
    }

    return { user, booted, loading, error, isAuthenticated, bootstrap, login, logout };
});
